import { useFormik } from 'formik';
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Difficulties, Statuses } from '../../domain/Constants';
import { getCourses } from '../../store/courseSlice';
import { createProgram, getPrograms, updateProgram } from '../../store/programSlice';
import Loading from '../common/Loading';
import BreadcrumbNav from '../curriculum/BreadcrumbNav';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Combobox } from '../ui/combobox';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { IconBook, IconClock, IconPlus } from '@tabler/icons-react';
import { Link } from 'react-router-dom';

const CourseCard = ({ course }) => (
    <Link
        to={`/curriculum/courses/${course.id}`}
        className="text-primary hover:text-primary/80"
    >
        <Card
            key={course.id}
            className="h-full cursor-pointer transition-all duration-200 hover:shadow-lg border-border hover:border-primary/20"
        >
            {course.image && (
                <div className="aspect-video w-full overflow-hidden rounded-t-lg">
                    <img
                        src={course.image}
                        alt={course.name}
                        className="h-full w-full object-cover"
                    />
                </div>
            )}
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    {course.name}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <CardDescription className="text-muted-foreground mb-2">
                   {course.description || 'No description available'}
                </CardDescription>
                <CardDescription className="text-muted-foreground">
                    {course.lessonCount || 0} lessons
                </CardDescription>
            </CardContent>
        </Card>
    </Link>
);

const ProgramForm = () => {
    const [file, setFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const loading = useSelector(state => state.program.loading);
    const programs = useSelector(state => state.program.list);
    const courses = useSelector(state => state.course.list);
    const fileRef = useRef();
    const navigate = useNavigate();
    const { programId } = useParams();
    const dispatch = useDispatch();

    // Check if we're editing an existing program
    const isEditing = programId && programId !== 'new';
    const currentProgram = isEditing ? programs.find(p => p.id === parseInt(programId)) : null;

    // Get courses for this program
    const programCourses = isEditing ? 
        courses.filter(c => {
            const courseProgramId = c.programId || c.program_id;
            return courseProgramId === parseInt(programId);
        }).filter((course, index, self) => 
            index === self.findIndex(c => c.id === course.id)
        ) : [];

    useEffect(() => {
        if (isEditing) {
            dispatch(getPrograms());
            dispatch(getCourses(programId));
        }
    }, [dispatch, isEditing, programId]);

    // Handle navigation after successful submission
    useEffect(() => {
        if (isSubmitting && !loading) {
            navigate('/curriculum');
            setIsSubmitting(false);
        }
    }, [isSubmitting, loading, navigate]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            description: currentProgram ? currentProgram.description : '',
            imageId: currentProgram ? currentProgram.imageId : null,
            name: currentProgram ? currentProgram.name : '',
            featured: currentProgram ? currentProgram.featured : false,
            difficulty: currentProgram ? currentProgram.difficulty : Difficulties[0].value,
            status: currentProgram ? currentProgram.status : Statuses[0].value,
        },
        validate: values => {
            const errors = {};

            if (!values.name || values.name.length === 0) {
                errors.name = 'Required';
            }

            if (!values.description || values.description.length === 0) {
                errors.description = 'Required';
            }

            if (!values.difficulty || values.difficulty.length === 0) {
                errors.difficulty = 'Required';
            }

            return errors;
        },
        onSubmit: (values, { resetForm }) => {
            onSubmit(values);
            if (fileRef.current) {
                fileRef.current.value = null;
            }
            if (!isEditing) {
                resetForm();
            }
        }
    });

    const handleCancel = (e) => {
        e.preventDefault();
        // Always go back to curriculum (programs list) for program forms
        navigate('/curriculum');
    };

    const handleAddCourse = () => {
        navigate(`/curriculum/programs/${programId}/courses/new`);
    };

    const onSubmit = (values) => {
        setIsSubmitting(true);

        if (isEditing) {
            dispatch(
                updateProgram(file, {
                    id: currentProgram.id,
                    description: values.description,
                    difficulty: values.difficulty,
                    featured: values.featured,
                    imageId: values.imageId,
                    name: values.name,
                    status: values.status,
                })
            );
            return;
        }

        dispatch(
            createProgram(file, {
                description: values.description,
                difficulty: values.difficulty,
                featured: values.featured,
                imageId: values.imageId,
                name: values.name,
                status: values.status,
            })
        );
    }

    const onChangeFile = (event) => {
        let newFile = event.target.files[0];
        setFile(newFile);
    }

    if (isEditing && !currentProgram) {
        return <div className="container-fluid p-0">Loading...</div>;
    }

    return (
        <div className="space-y-6">
            <BreadcrumbNav
                items={[
                    { label: 'Curriculum', href: '/curriculum' },
                    { label: isEditing ? `Edit ${currentProgram?.name}` : 'Create Program', href: null }
                ]}
            />

            <Card>
                <CardHeader>
                    <CardTitle className="text-xl font-semibold">
                        {isEditing ? 'Edit Program' : 'Create New Program'}
                    </CardTitle>
                </CardHeader>

                <CardContent>
                    <form onSubmit={formik.handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Name</label>
                                <Input
                                    type="text"
                                    name="name"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.name}
                                    placeholder="Enter program name..."
                                />
                                {formik.touched.name && formik.errors.name && (
                                    <div className="text-sm text-destructive">{formik.errors.name}</div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Difficulty</label>
                                <Combobox
                                    options={Difficulties}
                                    value={formik.values.difficulty}
                                    onChange={selected => formik.setFieldValue("difficulty", selected.value)}
                                    onBlur={formik.handleBlur}
                                    placeholder="Select difficulty"
                                />
                                {formik.touched.difficulty && formik.errors.difficulty && (
                                    <div className="text-sm text-destructive">{formik.errors.difficulty}</div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Status</label>
                                <Combobox
                                    options={Statuses}
                                    value={formik.values.status}
                                    onChange={selected => formik.setFieldValue("status", selected.value)}
                                    onBlur={formik.handleBlur}
                                    placeholder="Select status"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Description</label>
                            <textarea
                                rows={4}
                                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                name="description"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.description}
                                placeholder="Enter program description..."
                            />
                            {formik.touched.description && formik.errors.description && (
                                <div className="text-sm text-destructive">{formik.errors.description}</div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Image</label>
                                <input
                                    type="file"
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    name="image"
                                    ref={fileRef}
                                    onChange={onChangeFile}
                                    accept="image/*"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-foreground">Featured</label>
                                <div className="flex items-center space-x-2 pt-2">
                                    <Switch
                                        name="featured"
                                        checked={formik.values.featured}
                                        onCheckedChange={(checked) => formik.setFieldValue("featured", checked)}
                                    />
                                    <label className="text-sm text-muted-foreground">
                                        Mark as featured program
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-between pt-4">
                            <div className="flex items-center gap-2">
                                <Button
                                    type="submit"
                                    disabled={loading || isSubmitting}
                                >
                                    {isEditing ? "Update" : "Create"}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleCancel}
                                    disabled={loading || isSubmitting}
                                >
                                    Cancel
                                </Button>
                            </div>

                            {(loading || isSubmitting) && <Loading />}
                        </div>
                    </form>
                </CardContent>
            </Card>

            {/* Course List Section - Only show when editing existing program */}
            {isEditing && currentProgram && (
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold tracking-tight">Courses</h2>
                            <p className="text-muted-foreground">
                                Manage courses for {currentProgram.name}
                            </p>
                        </div>
                        <Button onClick={handleAddCourse}>
                            <IconPlus className="mr-2 h-4 w-4" />
                            Add Course
                        </Button>
                    </div>

                    {programCourses.length === 0 ? (
                        <div className="text-center py-12">
                            <IconBook className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                            <h3 className="text-lg font-semibold mb-2">No courses yet</h3>
                            <p className="text-muted-foreground mb-4">
                                Start building your program by adding your first course
                            </p>
                            <Button onClick={handleAddCourse} variant="outline">
                                <IconPlus className="mr-2 h-4 w-4" />
                                Add First Course
                            </Button>
                        </div>
                    ) : (
                        <div className="grid gap-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                            {programCourses.map((course) => (
                                <CourseCard key={course.id} course={course} />
                            ))}
                        </div>
                    )}
                </div>
            )}


        </div>
    );
};

export default ProgramForm; 